import React, { useCallback } from 'react';

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import mapboxgl from 'mapbox-gl';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { AddCircle } from '@mui/icons-material';
import { Box, IconButton, List, Stack } from '@mui/material';
import Joyride from 'react-joyride';

import MapIcon from '../../../../MapIcon';
import BuildingItem from './BuildingItem';
import CustomLabel from '../../../../shared/atoms/CustomLabel';
import CustomTooltip from '../../../../shared/atoms/CustomTooltip';
import { buildingIconURL } from '../../../../../utils/iconUrls';
import { useConfirmation } from '../../../../../context/ConfirmationContext';

const handleCursorStyle = isPlacingBuilding => {
  window.map.getCanvas().style.cursor = isPlacingBuilding
    ? `url('${buildingIconURL}') 16 16, auto`
    : '';
};

const BUILDING_OBJ = (building, mode) => ({
  id: uuidv4(),
  name: building?.name || '',
  description: building?.description || '',
  lat: null,
  lng: null,
  edit: true,
  mode: mode === 'all' ? mode : null,
  clone_id: mode === 'all' ? building?.id : null,
});

const MapBuildingsManagement = ({
  disabled,
  buildings,
  setFieldValue,
  handleRemoveBuilding,
  handleChange,
  handleBlur,
  setFieldTouched,
  errors,
  touched,
  replaceBuilding,
  updateIconsOnScreen,
  editingBuilding,
  setEditingBuilding,
  readyBuildingRef,
  iconElRef,
  markerRef,
}) => {
  const { t } = useTranslation();
  const { openConfirmation } = useConfirmation();

  const steps = [
    {
      target: '.mapboxgl-map',
      content: t('tools_menu.location.position_building'),
      placement: 'bottom',
      hideFooter: true,
      disableBeacon: true,
      offset: -200,
      styles: {
        options: {
          zIndex: 1203,
          arrowColor: 'transparent',
        },
      },
    },
  ];

  const createBuilding = useCallback(
    newBuilding => {
      const el = document.createElement('div');

      const iconEl = createPortal(
        <MapIcon
          id={newBuilding.id}
          name={newBuilding.name}
          src={buildingIconURL}
        />,
        el,
        newBuilding.id,
      );

      const newMarker = new mapboxgl.Marker(el)
        .setLngLat([newBuilding.lng, newBuilding.lat])
        .addTo(window.map);

      newMarker.getElement().setAttribute('id', newBuilding.id);

      iconElRef.current.set(newBuilding.id, iconEl);
      markerRef.current.set(newBuilding.id, newMarker);

      readyBuildingRef.current = null;
      replaceBuilding(newBuilding);
    },
    [iconElRef, markerRef, readyBuildingRef, replaceBuilding],
  );

  const drawNewBuilding = useCallback(
    coordinates => {
      const newBuilding = {
        ...readyBuildingRef.current,
        lat: coordinates.lat,
        lng: coordinates.lng,
      };

      window.Draw.add({
        geometry: {
          coordinates: [coordinates.lng, coordinates.lat],
          type: 'Point',
        },
        properties: newBuilding,
        type: 'Feature',
      });

      createBuilding(newBuilding);
    },
    [createBuilding, readyBuildingRef],
  );

  const handleMapClick = useCallback(
    e => {
      const coordinates = e.lngLat;
      drawNewBuilding(coordinates);
      handleCursorStyle(false);
      window.map.off('click', handleMapClick);
    },
    [drawNewBuilding],
  );

  const handleAddBuilding = () => {
    const newBuilding = {
      ...BUILDING_OBJ(),
    };
    setEditingBuilding(newBuilding);
    setFieldValue('buildings', [...buildings, newBuilding]);
  };

  const handleConfirmBuildingEdit = building => {
    if (!editingBuilding.lat && !editingBuilding.lng) {
      readyBuildingRef.current = building;
      handleCursorStyle(true);
      window.map.on('click', handleMapClick);
    } else {
      markerRef.current.get(building.id).remove();
      createBuilding(building);
    }
    setEditingBuilding(null);
  };

  const handleCancelBuildingEdit = () => {
    if (!editingBuilding.lat && !editingBuilding.lng)
      handleRemoveBuilding(editingBuilding.id, null, true);
    else replaceBuilding(editingBuilding);
    setEditingBuilding(null);
  };

  const handleBuildingEdit = building => {
    window.Draw.changeMode('simple_select', { featureIds: [] });
    updateIconsOnScreen();
    setEditingBuilding(building);
  };

  const onClone = useCallback(
    (building, mode) => {
      const newBuilding = {
        ...BUILDING_OBJ(building, mode),
      };
      setEditingBuilding(newBuilding);
      setFieldValue('buildings', [...buildings, newBuilding]);
      setTimeout(
        () =>
          setFieldTouched(`buildings[${buildings.length}].name`, true, true),
        100,
      );
    },
    [buildings, setEditingBuilding, setFieldTouched, setFieldValue],
  );

  const handleBuildingClone = useCallback(
    building => {
      if (!building.floors?.length) return onClone(building, 'only');
      openConfirmation({
        message: t('tools_menu.location.clone_building_confirm', {
          name: building.name,
        }),
        options: [
          {
            onClick: async () => onClone(building, 'only'),
            label: t('tools_menu.location.clone_building_only'),
            minWidth: '400px',
          },
          {
            onClick: async () => onClone(building, 'all'),
            label: t('tools_menu.location.clone_building_all'),
            minWidth: '400px',
          },
        ],
        preventClickOutside: true,
      });
    },
    [onClone, openConfirmation, t],
  );

  return (
    <>
      <Joyride
        steps={steps}
        run={readyBuildingRef.current}
        spotlightClicks
        disableOverlayClose
        styles={{
          buttonNext: {
            display: 'none',
          },
          buttonClose: {
            display: 'none',
          },
          tooltipContainer: {
            margin: -15,
          },
          beacon: {
            display: 'none',
          },
          spotlight: {
            marginLeft: '220px',
            marginTop: '10px',
            pointerEvents: 'auto',
          },
          overlay: {
            pointerEvents: 'none',
          },
        }}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <CustomLabel>{t('tools_menu.location.buildings')}</CustomLabel>
        <CustomTooltip title="Add">
          <Box>
            <IconButton
              color="primary"
              onClick={handleAddBuilding}
              disabled={
                disabled || !!editingBuilding || !!readyBuildingRef.current
              }
            >
              <AddCircle />
            </IconButton>
          </Box>
        </CustomTooltip>
      </Stack>
      <List dense>
        {buildings.map((building, index) => (
          <BuildingItem
            key={building.id}
            handleRemoveBuilding={handleRemoveBuilding}
            handleCancelBuildingEdit={handleCancelBuildingEdit}
            handleConfirmBuildingEdit={() =>
              handleConfirmBuildingEdit(building)
            }
            handleBuildingEdit={handleBuildingEdit}
            handleBuildingClone={handleBuildingClone}
            isEditing={building.id === editingBuilding?.id}
            isLast={buildings.length - 1 === index}
            disabled={
              !!readyBuildingRef.current || disabled || !!editingBuilding
            }
            building={building}
            index={index}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
          />
        ))}
      </List>
      {buildings
        .filter(item => iconElRef.current.get(item.id))
        .map(item => iconElRef.current.get(item.id))}
    </>
  );
};

MapBuildingsManagement.propTypes = {
  disabled: PropTypes.bool.isRequired,
  buildings: PropTypes.array.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setEditingBuilding: PropTypes.func.isRequired,
  handleRemoveBuilding: PropTypes.func.isRequired,
  updateIconsOnScreen: PropTypes.func.isRequired,
  replaceBuilding: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  editingBuilding: PropTypes.object,
  readyBuildingRef: PropTypes.object.isRequired,
  iconElRef: PropTypes.object.isRequired,
  markerRef: PropTypes.object.isRequired,
};

export default MapBuildingsManagement;
